import React, { useState, useEffect, useRef } from 'react';

import { CloseIcon, SelectionWarningIcon, KeepOpenIcon } from './Styles/Icons';
import {
	InputGroup,
	TextArea,
	ButtonGroup,
	Button,
	FilledErrorButton,
	FlexColumn,
	Dialog,
	DialogActions,
	DialogTitle,
	DialogContent,
	CircularButton,
	ReasonChipRow,
	ReasonChip,
	Subtitle,
	ItemsListContainer,
	ItemsListHeader,
	ItemsListHeaderLabel,
	ItemsListHeaderCount,
	ItemRow,
	ItemCheckbox,
	ItemName,
	ItemStatus,
	ItemPrice,
	FooterNote,
	SelectionWarning,
	KeepOpenNote,
} from './Styles/CancelOrderDialogBox';

import { formatCurrency } from '../../commons/utils';
import { CancelReasons, COMMENT_MAX_LENGTH, ShippingOptions, isCancellableLine } from './Utils';

const STATUS_LABELS = {
	[ShippingOptions.PENDING]: 'Placed',
	[ShippingOptions.PROCESSING]: 'Processing',
	[ShippingOptions.BACKORDER]: 'Backordered',
};
const REASON_DESCRIPTIONS = {
	'Out of stock': 'These items are out of stock and can’t be fulfilled.',
	'Item discontinued': 'These items have been discontinued by the manufacturer.',
	'Customer requested': 'Cancelled at the customer’s request.',
	'Pricing error': 'These items were ordered at incorrect pricing and have been cancelled.',
	'Duplicate order': 'These items duplicate another order already placed with us.',
};

const CancelOrderDialogBox = ({ open, orderId, orderLines = [], onTrue, onFalse }) => {
	const [comment, setComment] = useState('');
	const [selectedReason, setSelectedReason] = useState(null);
	const [selectedIds, setSelectedIds] = useState([]);
	const [submitting, setSubmitting] = useState(false);
	const selectAllRef = useRef(null);

	const openLines = orderLines.filter(isCancellableLine);
	const selectedLines = openLines.filter((line) => selectedIds.includes(line._id));
	const selectedTotal = selectedLines.reduce((sum, line) => sum + (line.price || 0), 0);
	const allSelected = openLines.length > 0 && selectedIds.length === openLines.length;

	const titleText = allSelected ? `Cancel Order ${orderId || ''}` : `Cancel Items · ${orderId || ''}`;
	const subtitleText = selectedIds.length
		? `${selectedIds.length} item${selectedIds.length === 1 ? '' : 's'} selected · ${formatCurrency(selectedTotal)}`
		: 'No items selected';
	const cancelCount = Math.max(selectedIds.length, 1);
	const goLabel = allSelected
		? `Cancel Order (${selectedIds.length} Item${selectedIds.length === 1 ? '' : 's'})`
		: `Cancel ${cancelCount} Item${cancelCount === 1 ? '' : 's'}`;

	useEffect(() => {
		if (open) {
			setComment('');
			setSelectedReason(null);
			setSelectedIds(orderLines.filter(isCancellableLine).map((line) => line._id));
			setSubmitting(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (selectAllRef.current)
			selectAllRef.current.indeterminate = selectedIds.length > 0 && !allSelected;
	}, [selectedIds, allSelected]);

	const onReasonSelect = (reason) => {
		setSelectedReason(reason);
		setComment(REASON_DESCRIPTIONS[reason] || reason);
	};

	const onCommentChange = (value) => {
		setComment(value);
		if (value !== REASON_DESCRIPTIONS[selectedReason])
			setSelectedReason(null);
	};

	const onItemToggle = (lineId) => {
		setSelectedIds((prev) => (prev.includes(lineId) ? prev.filter((id) => id !== lineId) : [...prev, lineId]));
	};

	const onSelectAllToggle = () => {
		setSelectedIds(allSelected ? [] : openLines.map((line) => line._id));
	};

	const onTrueHandler = async () => {
		if (!comment.trim() || !selectedIds.length || submitting)
			return;

		setSubmitting(true);
		try {
			await onTrue(selectedIds, comment);
		} finally {
			setSubmitting(false);
		}
	};

	const onFalseHandler = () => {
		onFalse();
	};

	return (
		<Dialog
			open={open}
			onClose={() => onFalseHandler()}
			fullWidth
		>
			<DialogTitle>
				<FlexColumn>
					{titleText}
					<Subtitle>{subtitleText}</Subtitle>
				</FlexColumn>

				<ButtonGroup alignRight>
					<CircularButton
						error
						outline
						onClick={() => onFalseHandler()}
					>
						<CloseIcon />
					</CircularButton>
				</ButtonGroup>
			</DialogTitle>

			<DialogContent>
				<FlexColumn>
					<ItemsListContainer>
						<ItemsListHeader>
							<ItemCheckbox
								ref={selectAllRef}
								checked={allSelected}
								onChange={() => onSelectAllToggle()}
							/>
							<ItemsListHeaderLabel>Select all open items</ItemsListHeaderLabel>
							<ItemsListHeaderCount>{`${selectedIds.length} of ${openLines.length}`}</ItemsListHeaderCount>
						</ItemsListHeader>

						{openLines.map((line) => (
							<ItemRow
								key={line._id}
								unchecked={!selectedIds.includes(line._id)}
							>
								<ItemCheckbox
									checked={selectedIds.includes(line._id)}
									onChange={() => onItemToggle(line._id)}
								/>
								<ItemName
									unchecked={!selectedIds.includes(line._id)}
									title={line.name}
								>
									{line.name}
								</ItemName>
								<ItemStatus>{STATUS_LABELS[line.status] || line.status}</ItemStatus>
								<ItemPrice>{formatCurrency(line.price || 0)}</ItemPrice>
							</ItemRow>
						))}
					</ItemsListContainer>

					{!selectedIds.length ? (
						<SelectionWarning>
							<SelectionWarningIcon />
							Select at least one item to cancel.
						</SelectionWarning>
					) : !allSelected ? (
						<KeepOpenNote>
							<KeepOpenIcon />
							{`${openLines.length - selectedIds.length} item${openLines.length - selectedIds.length === 1 ? '' : 's'} will stay open and can still be fulfilled.`}
						</KeepOpenNote>
					) : null}

					<ReasonChipRow>
						{CancelReasons.map((reason) => (
							<ReasonChip
								key={reason}
								label={reason}
								selected={selectedReason === reason}
								onClick={() => onReasonSelect(reason)}
							/>
						))}
					</ReasonChipRow>

					<InputGroup
						label='Reason for cancellation'
						markRequired={true}
						maxLength={COMMENT_MAX_LENGTH}
						inputLength={comment.length}
						message={`This reason will be shown to the customer on ${selectedIds.length === 1 ? 'the cancelled item' : `all ${selectedIds.length} cancelled items`}.`}
					>
						<TextArea
							name='cancel_order_comment'
							placeholder='Enter a reason for cancellation'
							value={comment}
							inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
							onChange={(e) => onCommentChange(e.target.value)}
						/>
					</InputGroup>
				</FlexColumn>
			</DialogContent>

			<DialogActions style={{ margin: '0 0.5rem', justifyContent: 'space-between' }}>
				<FooterNote>This can't be undone.</FooterNote>

				<ButtonGroup alignRight>
					<Button
						primary
						outline
						onClick={(e) => onFalseHandler()}
					>
						Keep Order
					</Button>
					<FilledErrorButton
						disabled={!comment.trim() || !selectedIds.length || submitting}
						onClick={(e) => onTrueHandler()}
					>
						{goLabel}
					</FilledErrorButton>
				</ButtonGroup>
			</DialogActions>
		</Dialog>
	);
};

export default CancelOrderDialogBox;
